import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStripe } from '@stripe/react-stripe-js';
import { useSubscriptionContext } from './Subscription.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { SubscriptionManagerService } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.service';
import type {
  tProducts,
  tInvoicePreview,
} from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.types';
import {
  isBankDeclineError,
  resolveBillingErrorMessage,
  type tBillingErrorPayload,
} from 'src/utils/resolveBillingError';
import { LLog } from 'src/lib/logging';
import type {
  tAmountDueToday,
  tSeatPurchase,
  tSeatPurchaseOutcome,
  tSeatQuote,
} from './useSeatPurchase.types';
import { resolveSeatQuote } from './useSeatPurchase.functions';

const LOG_CONTEXT = {
  component: 'useSeatPurchase',
  method: 'useSeatPurchase',
  operation: 'seat-purchase',
};

/**
 * One seat: what it costs, what is due today, and buying it.
 *
 * Consumed by the Team tab's add-teammate flow and by the Subscription tab's
 * ManageUsers modal, which is what makes "Team and Subscription cannot quote
 * different numbers" structural rather than a convention.
 *
 * It deliberately does NOT depend on `SubscriptionManager.hook` (~2,900 lines owning
 * plan selection, coupons and checkout state) — only on its stateless service module,
 * so the endpoints stay in one place without dragging that state along.
 *
 * @param options.enabled fetch and quote only when true. The add-teammate flow passes
 *   the at-limit state, so a workspace with a free seat issues no products call and no
 *   preview call — no extra network of any kind.
 */
export function useSeatPurchase(options: { enabled: boolean }): tSeatPurchase {
  const { enabled } = options;
  // resolveBillingError's copy lives in this namespace and it documents the binding
  // as mandatory. `settings/subscription` does not exist, so every resolved billing
  // message rendered its raw key.
  const { t } = useTranslation('settings/SubscriptionManager');
  const stripe = useStripe();
  const { subscription, isLoadingSubscription, hasSubscriptionData } =
    useSubscriptionContext();
  const { currentWorkspace } = useWorkspaceContext();
  const workspaceId = currentWorkspace?.id ?? '';

  const [products, setProducts] = useState<tProducts | null>(null);
  const [hasProductsError, setHasProductsError] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [amountDueToday, setAmountDueToday] = useState<tAmountDueToday>({
    known: false,
  });
  const [isLoadingAmountDueToday, setIsLoadingAmountDueToday] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);

  // Minted per submit attempt and rotated on every terminal outcome. The id may not
  // be cached across attempts: the cart is fixed at one SEATS unit, so the payload
  // hash is byte-identical every time and leadbilling replays a completed intent as
  // an idempotent 200 — no Stripe call, no seat, and a success the UI would believe.
  const purchaseIntentIdRef = useRef<string>('');
  const isPurchasingRef = useRef(false);

  const rotatePurchaseIntent = useCallback(() => {
    purchaseIntentIdRef.current = '';
  }, []);

  // Products are NOT on SubscriptionProvider — they are local state inside
  // SubscriptionManager.hook, fetched only for workspaces on selected plans, and the
  // Team tab never mounts that hook. Without its own fetch this hook could never
  // resolve a price and every at-limit user would land in no-quote.
  useEffect(() => {
    if (!enabled || !workspaceId) return;

    let cancelled = false;
    setIsLoadingProducts(true);
    setHasProductsError(false);

    SubscriptionManagerService.getProducts(workspaceId)
      .then((response: tProducts) => {
        if (cancelled) return;
        setProducts(response);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // A catalogue we could not read is a no-quote, never a zero.
        setHasProductsError(true);
        LLog.error(
          LOG_CONTEXT,
          'Could not read the products catalogue for a seat quote',
          error instanceof Error ? error : undefined,
          { workspaceId },
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoadingProducts(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, workspaceId]);

  const isReady =
    !enabled ||
    (!isLoadingSubscription && hasSubscriptionData && !isLoadingProducts);

  const quote = useMemo<tSeatQuote>(
    () =>
      resolveSeatQuote({
        status: subscription?.status,
        planCode: subscription?.planCode,
        currency: subscription?.currency,
        purchasedSeatsCost: subscription?.purchasedSeatsCost,
        allRelatedProducts: products?.allRelatedProducts,
        availableForPurchase: products?.availableForPurchase,
        hasProductsError,
      }),
    [subscription, products, hasProductsError],
  );

  // The amount due today comes from the server or not at all.
  useEffect(() => {
    if (!enabled || !workspaceId || !quote.available || !quote.productId) {
      setAmountDueToday({ known: false });
      return;
    }

    const controller = new AbortController();
    // A cancelled flag as well as the abort, matching the sibling products effect: an
    // aborted request still settles, and its late .catch()/.finally() would otherwise
    // clear a fresher amount or write state after teardown.
    let cancelled = false;
    setIsLoadingAmountDueToday(true);

    SubscriptionManagerService.getInvoicePreview(
      workspaceId,
      { products: [{ product_id: quote.productId, quantity: 1 }] },
      controller.signal,
    )
      .then((response: tInvoicePreview) => {
        if (cancelled) return;
        const preview = response?.data?.data;
        if (typeof preview?.total_amount !== 'number') {
          setAmountDueToday({ known: false });
          return;
        }
        setAmountDueToday({
          known: true,
          amountCents: preview.total_amount,
          currency: preview.currency || quote.currency,
        });
      })
      .catch(() => {
        if (cancelled) return;
        // Unavailable is a state, not a zero: the control must not promise a figure
        // the invoice will not match.
        setAmountDueToday({ known: false });
      })
      .finally(() => {
        if (!cancelled) setIsLoadingAmountDueToday(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `quote` is a fresh object each render; the identity that matters is the product
    // it resolved to and whether it resolved at all.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, workspaceId, quote.available, (quote as { productId?: string }).productId]);

  const purchaseOneSeat = useCallback(async (): Promise<tSeatPurchaseOutcome> => {
    if (!quote.available || !quote.productId || !workspaceId) {
      return {
        status: 'failed',
        message: t('errors.generic'),
        declined: false,
      };
    }

    // Guarded on a ref, not the state: two clicks in the same tick both read the
    // pre-render state value and both would fire.
    if (isPurchasingRef.current) {
      // A real message: this reaches an alert, and an empty string renders a blank
      // toast that reads as a broken page rather than "your purchase is running".
      return {
        status: 'failed',
        message: t('errors.purchaseInProgress'),
        declined: false,
      };
    }
    isPurchasingRef.current = true;
    setIsPurchasing(true);

    if (!purchaseIntentIdRef.current) {
      purchaseIntentIdRef.current = crypto.randomUUID();
    }
    const purchaseIntentId = purchaseIntentIdRef.current;
    const line = { product_id: quote.productId, quantity: 1 };

    try {
      const response = await SubscriptionManagerService.purchaseProduct(
        workspaceId,
        { products: [line], purchaseIntentId },
      );

      if (response?.data?.status === 'requires_action') {
        const clientSecret = response.data.payment_intent_client_secret;

        // A bank asking for 3DS with nothing to confirm is a failure. The branch this
        // replaces fell through to success here.
        if (!clientSecret || !stripe) {
          // Deliberately NOT rotating: the server answered `requires_action`, so it
          // already created a Stripe invoice against THIS intent. A fresh id would
          // start a second one. Reusing it lets the server reclaim the pending row.
          return {
            status: 'failed',
            message: resolveBillingErrorMessage(
              { error_code: 'payment_failed' } as tBillingErrorPayload,
              t,
            ),
            // A bank asking for 3DS with nothing to confirm is not the bank saying no.
            declined: false,
          };
        }

        const result = await stripe.confirmPayment({
          clientSecret,
          redirect: 'if_required' as const,
        });

        if (result?.error) {
          // Rotating IS correct here and only here: the bank refused during 3DS, so
          // Stripe captured nothing. Reusing the id would hit the server's 5-minute
          // stale-pending window and return 409 `purchase_in_progress`, which would
          // make the "Try again" affordance dead for five minutes — the one thing
          // this decline path exists to offer.
          rotatePurchaseIntent();
          // Built in the shape the resolver and the predicate both read, so the copy
          // and the "was this a decline?" answer come from the same payload.
          const declinePayload: tBillingErrorPayload = {
            decline_code: result.error.decline_code,
            message: result.error.code ?? result.error.message,
          };
          return {
            status: 'failed',
            message: resolveBillingErrorMessage(declinePayload, t),
            errorCode: result.error.decline_code ?? result.error.code,
            declined: isBankDeclineError(declinePayload),
          };
        }

        // Re-call with the SAME intent id: this is the server-side dedup authority,
        // and a fresh id here would charge twice.
        await SubscriptionManagerService.purchaseProduct(workspaceId, {
          products: [line],
          confirmStripe: false,
          purchaseIntentId,
        });
      }

      rotatePurchaseIntent();
      return { status: 'purchased' };
    } catch (error) {
      // Deliberately NOT rotating. This is the ambiguous failure — a timeout or a
      // dropped connection — where the server may already have charged. Reusing the
      // id is the ONLY thing that lets it dedup: a completed intent replays as
      // success, a failed one is reclaimed, a stale pending one is reclaimed without
      // a second Stripe call. A fresh id here is how you charge a customer twice.
      const payload = error as tBillingErrorPayload & { error_code?: string };
      return {
        status: 'failed',
        message: resolveBillingErrorMessage(payload, t),
        errorCode: payload?.error_code,
        declined: isBankDeclineError(payload),
      };
    } finally {
      isPurchasingRef.current = false;
      setIsPurchasing(false);
    }
  }, [quote, workspaceId, stripe, t, rotatePurchaseIntent]);

  return {
    isReady,
    quote,
    amountDueToday,
    isLoadingAmountDueToday,
    isPurchasing,
    purchaseOneSeat,
    resetPurchaseIntent: rotatePurchaseIntent,
  };
}
